import { DATE_TIME_FORMAT } from '@lib/constants/common'
import Checkbox from '@components/book-trial-class/checkbox'
import {
  ClassRescheduleOptionsEnum,
  RESCHEDULE_OPTIONS_TOOLTIP,
} from '@lib/constants/reschedule-class'
import { formatTimeByTimezone } from '@lib/utils/date.utils'
import classNames from 'classnames'
import { Dispatch, SetStateAction } from 'react'
import RescheduleModalTitle from '@components/reschedule-course-class/reschedule-modal-title'
import Button from '@components/atoms/button'
import Tooltip from '@components/common/tooltip'
import AlertMessage from '@components/atoms/alert-message'
import { Info } from 'react-feather'

type OptionProps = {
  onClick: () => void
  isNextClassOption?: boolean
  isSelected?: boolean
  disabled?: boolean
  timezone: string
  tooltip: string
  isTeacher: boolean
  nextClassTimestampInBatchSchedule?: string
}

const Option = ({
  onClick,
  isNextClassOption = false,
  isSelected = false,
  disabled = false,
  timezone,
  tooltip,
  isTeacher,
  nextClassTimestampInBatchSchedule = '',
}: OptionProps) => (
  <div
    className={classNames(
      'flex items-center justify-between px-5 py-3 border  rounded-lg h-16',
      {
        'cursor-pointer hover:border-orange hover:bg-orange-200': !disabled,
        'cursor-not-allowed bg-grey-100 border-grey-400': disabled,
        'border-orange': isSelected && !disabled,
        'border-grey-500': !isSelected && !disabled,
      }
    )}
    onClick={disabled ? undefined : onClick}
  >
    <div className="flex items-center space-x-5">
      <div className="flex items-center justify-center">
        <Checkbox
          isRounded
          readOnly
          size="lg"
          name="reschedule_option"
          checked={isSelected}
          id={nextClassTimestampInBatchSchedule}
        />
      </div>
      <div className={classNames({ 'text-grey-700': disabled })}>
        {isNextClassOption ? (
          <>
            <p>
              {isTeacher
                ? 'I will take this class next time'
                : 'I want to attend this class next time'}
            </p>

            <p className="font-600">
              {formatTimeByTimezone(
                nextClassTimestampInBatchSchedule,
                timezone,
                DATE_TIME_FORMAT
              )}
            </p>
          </>
        ) : (
          <>I want to reschedule this class at another time</>
        )}
      </div>
    </div>
    <div className="flex items-center space-x-3 flex-shrink-0">
      {disabled && (
        <span className="inline-flex items-center rounded border border-red-200 px-2 py-0.5 text-xs font-600 text-red-200">
          Disabled
        </span>
      )}
      <Tooltip content={<p className="leading-tight">{tooltip}</p>} showOnHover>
        <Info className="w-5 cursor-pointer text-grey-700" />
      </Tooltip>
    </div>
  </div>
)

type Props = {
  selectedRescheduleOption: ClassRescheduleOptionsEnum
  setSelectedRescheduleOption: Dispatch<
    SetStateAction<ClassRescheduleOptionsEnum>
  >
  rescheduleRequestedBy: 'student' | 'teacher' | null
  isWithinFirst8: boolean
  lessonNumber: string | number
  lessonTitle: string
  onContinue: () => void
  onGoBack: () => void
  timezone: string
  isLoading: boolean
  isTeacher: boolean
  nextClassTimestampInBatchSchedule: string
}

const Selection = ({
  lessonTitle,
  lessonNumber: lessonNumber,
  selectedRescheduleOption,
  setSelectedRescheduleOption,
  onGoBack,
  onContinue,
  timezone,
  isLoading,
  isTeacher,
  rescheduleRequestedBy,
  isWithinFirst8,
  nextClassTimestampInBatchSchedule,
}: Props) => (
  <div className="flex flex-col space-y-5">
    <RescheduleModalTitle
      lessonNumber={lessonNumber}
      lessonTitle={lessonTitle}
    />
    {rescheduleRequestedBy && (
      <p className="text-grey-800 font-600">
        Reason selected:{' '}
        {rescheduleRequestedBy === 'student' ? 'Student' : 'Teacher'} requested
        to reschedule
      </p>
    )}
    <p className="font-600">Please select your preference to continue</p>
    <Option
      onClick={() =>
        setSelectedRescheduleOption(ClassRescheduleOptionsEnum.nextTime)
      }
      isNextClassOption
      isSelected={
        selectedRescheduleOption === ClassRescheduleOptionsEnum.nextTime
      }
      disabled={
        isTeacher && rescheduleRequestedBy === 'student' && isWithinFirst8
      }
      timezone={timezone}
      tooltip={
        RESCHEDULE_OPTIONS_TOOLTIP[isTeacher ? 'teacher' : 'student']
          .rescheduleToNextClass
      }
      isTeacher={isTeacher}
      nextClassTimestampInBatchSchedule={nextClassTimestampInBatchSchedule}
    />
    <Option
      onClick={() =>
        setSelectedRescheduleOption(ClassRescheduleOptionsEnum.beforeNextClass)
      }
      isSelected={
        selectedRescheduleOption === ClassRescheduleOptionsEnum.beforeNextClass
      }
      timezone={timezone}
      tooltip={
        RESCHEDULE_OPTIONS_TOOLTIP[isTeacher ? 'teacher' : 'student']
          .rescheduleBeforeNextClass
      }
      isTeacher={isTeacher}
    />
    {isTeacher && rescheduleRequestedBy === 'student' && isWithinFirst8 && (
      <AlertMessage type="danger" className="leading-tight">
        This is one of the student&apos;s first 8 classes - a critical retention
        window - so a class they asked to cancel can&apos;t be moved to the next
        class time from here. Ask the student to reschedule it from their
        dashboard, or raise a ticket: on your dashboard, click &quot;Chat with
        us&quot;, choose &quot;Paid student / course Class issue&quot;, then
        &quot;Course Class&quot;, then &quot;Student requested to cancel (first
        8 classes)&quot;.
      </AlertMessage>
    )}
    {!isTeacher && (
      <AlertMessage type="info" className="leading-tight">
        You will not lose any points for rescheduling this class
      </AlertMessage>
    )}
    {isTeacher && rescheduleRequestedBy === 'teacher' && (
      <AlertMessage className="leading-tight">
        A penalty will be imposed if your cancellation limit has been reached.
      </AlertMessage>
    )}
    <div className="flex space-x-5">
      <Button
        disabled={!selectedRescheduleOption || isLoading}
        loading={isLoading}
        onClick={onContinue}
      >
        Continue
      </Button>
      <button
        className="hover:underline focus:outline-none font-500"
        onClick={onGoBack}
      >
        Go back
      </button>
    </div>
  </div>
)

export default Selection
